import { useContext } from "react";

import {
  Directory,
  FileRead,
  FileReadWithUrl,
  instanceOfDirectory,
  instanceOfFileReadWithUrl,
} from "@nens/rana/api-v1";

import { ProjectContext } from "@/stores/project-provider";
import { getParentPath } from "@/utils/get-file-parent-path";

import {
  useInvalidateFileQueries,
  useInvalidateFileQueriesIfOutdated,
  useUpdateFileQueries,
} from "./query-file";
import {
  useInvalidateFileHistoryQueries,
  useInvalidateFileHistoryQueriesIfOutdated,
} from "./query-file-history";

export const useInvalidateFilesAndHistoryQueriesIfOutdated = () => {
  const invalidateFileQueriesIfOutdated = useInvalidateFileQueriesIfOutdated();
  const invalidateFileHistoryQueriesIfOutdated = useInvalidateFileHistoryQueriesIfOutdated();

  return (finishedAt: Date, fileId: string) => {
    invalidateFileQueriesIfOutdated(finishedAt, fileId);
    invalidateFileHistoryQueriesIfOutdated(finishedAt, fileId);
  };
};

export const useInvalidateFilesAndHistoryQueries = () => {
  const invalidateFileQueries = useInvalidateFileQueries();
  const invalidateFileHistoryQueries = useInvalidateFileHistoryQueries();

  return (projectId: string, fileId: string) => {
    invalidateFileQueries(projectId, fileId);
    invalidateFileHistoryQueries(projectId, fileId);
  };
};

export const useUpdateFileAndInvalidateHistoryQueries = () => {
  const { id: projectId } = useContext(ProjectContext);
  const updateFileQueries = useUpdateFileQueries();
  const invalidateFileHistoryQueries = useInvalidateFileHistoryQueries();
  const invalidateFileQueries = useInvalidateFileQueries();

  return async ({
    beforeId,
    file,
  }: {
    beforeId: string;
    file: FileRead | FileReadWithUrl | Directory | null;
  }) => {
    const isMove = file && getParentPath(beforeId) !== getParentPath(file.id);
    const isRename = file && !isMove && beforeId && beforeId !== file.id;

    const promises = [];

    // FUTURE:
    // Supporting FileReadWithUrl and without is a bit of a weird design, but currently
    // all the mutations in the backend only return the FileRead. Thats not ideal of course
    // because now we have to invalidate instead of updating as you can see below
    // After https://github.com/nens/rana/issues/4441 has been merged this can
    // be refactored to only support FileReadWithUrl and then we can always update

    // If the file is deleted, is a directory or is a complete file with url
    if (!file || instanceOfFileReadWithUrl(file) || instanceOfDirectory(file)) {
      promises.push(updateFileQueries(beforeId, file));
    } else {
      promises.push(invalidateFileQueries(projectId, file.id));
    }

    // Invalidate before history query
    if (isMove || isRename || !file?.id) {
      promises.push(invalidateFileHistoryQueries(projectId, beforeId));
    }

    // Invalidate target history query
    if (file?.id) {
      promises.push(invalidateFileHistoryQueries(projectId, file.id));
    }

    await Promise.all(promises);
  };
};
